import {
  BoundaryWithSoftBoundary,
  DraftConfiguration,
  DraftConfigurationWithTextOrSpecialText,
  DraftDesignConfiguration,
  DraftError,
  ProductType,
  Rect,
  Size,
} from '@/types';
import { degreeToRad } from '@/utils/math/degreeToRad';
import { rotatePoint } from '@/utils/math/rotatePoint';
import { round } from '@/utils/math/round';
import { isEmbroideryMode } from '@/utils/params';
import { getFirstSymbol } from '@/utils/text/getFirstSymbol';
import { getHasSoftBoundary } from '@/model/printArea/getHasSoftBoundary';
import { getIsAdultDesignsVerboten } from '@/model/productType/getIsAdultDesignsVerboten';
import { getMaxScale } from './getMaxScale';
import {
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from './isDraftConfigurationType';
import { isPixelDraftConfiguration } from './isPixelDraftConfiguration';
import { rotatedSize } from './rotatedSize';

export function validateMinDpi(
  { printArea, printType, rotation, scale }: DraftConfiguration,
  size: Size
) {
  const maxScale = getMaxScale(size, rotation, printArea, printType, true);

  // maxScale is depending on the current size so add epsilon
  //  as a guard against rounding errors in floating-point math
  return scale > maxScale + Number.EPSILON;
}

function validateMaxScale(configuration: DraftConfiguration, size: Size) {
  return isPixelDraftConfiguration(configuration) && validateMinDpi(configuration, size);
}

function validateMaxBound(
  { printType: { size: printTypeSize }, rotation }: DraftConfiguration,
  size: Size
) {
  const configSize = rotatedSize(rotation, size);
  return configSize.width > printTypeSize.width || configSize.height > printTypeSize.height;
}

function getConfigurationCorners({ offset, rotation }: DraftConfiguration, size: Size) {
  const configurationCenter = {
    x: offset.x + size.width / 2,
    y: offset.y + size.height / 2,
  };

  const rotationInRad = degreeToRad(rotation);

  return [
    rotatePoint(offset.x, offset.y, rotationInRad, configurationCenter.x, configurationCenter.y),
    rotatePoint(
      offset.x + size.width,
      offset.y,
      rotationInRad,
      configurationCenter.x,
      configurationCenter.y
    ),
    rotatePoint(
      offset.x,
      offset.y + size.height,
      rotationInRad,
      configurationCenter.x,
      configurationCenter.y
    ),
    rotatePoint(
      offset.x + size.width,
      offset.y + size.height,
      rotationInRad,
      configurationCenter.x,
      configurationCenter.y
    ),
  ].map(({ x, y }) => ({ x: round(x), y: round(y) }));
}

const parsedSoftBoundaryRects = new Map<string, Rect[]>();

function getSoftBoundaryRects(boundary: BoundaryWithSoftBoundary) {
  const softBoundaryPath = boundary.soft.content.svg.path.d;
  const cachedResult = parsedSoftBoundaryRects.get(softBoundaryPath);
  if (cachedResult) {
    return cachedResult;
  }

  let result: Rect[];
  try {
    // this is not a svg parser, we can only create boundaries for very simple paths
    //  the assumption is that the path contains only absolute LineTo commands (L)
    //  we take the coordinates and create a rectangle around the min and max points
    result = softBoundaryPath.split('Z M').map((path) => {
      const coordinates = path
        .trim()
        .replace(/^M|L|Z$/g, '')
        .trim()
        .split(' ')
        .map((position) => position.split(','));

      const xCoordinates = coordinates.map(([x]) => parseFloat(x));
      const yCoordinates = coordinates.map(([, y]) => parseFloat(y));

      const minX = Math.min(...xCoordinates);
      const minY = Math.min(...yCoordinates);

      if (Number.isNaN(minX) || Number.isNaN(minY)) {
        throw new Error('Invalid path');
      }

      return {
        x: minX,
        y: minY,
        width: round(Math.max(...xCoordinates) - minX),
        height: round(Math.max(...yCoordinates) - minY),
      };
    });
  } catch (error) {
    // fallback to the hard boundary
    result = [{ x: 0, y: 0, ...boundary.size }];
  }

  parsedSoftBoundaryRects.set(softBoundaryPath, result);

  return result;
}

function validateBoundaries(configuration: DraftConfiguration, size: Size) {
  const configurationCorners = getConfigurationCorners(configuration, size);
  const boundary = configuration.printArea.boundary;

  if (getHasSoftBoundary(boundary)) {
    const xValues = configurationCorners.map(({ x }) => x);
    const yValues = configurationCorners.map(({ y }) => y);

    if (isEmbroideryMode()) {
      // soft boundary check - are any x or y values outside one side of the soft boundary
      return getSoftBoundaryRects(boundary).every(({ x, y, width, height }) => {
        return (
          xValues.some((xValue) => xValue < x) ||
          xValues.some((xValue) => xValue > x + width) ||
          yValues.some((yValue) => yValue < y) ||
          yValues.some((yValue) => yValue > y + height)
        );
      });
    } else {
      // soft boundary check - are all x or y values outside one side of the soft boundary
      return getSoftBoundaryRects(boundary).every(({ x, y, width, height }) => {
        return (
          xValues.every((xValue) => xValue < x) ||
          xValues.every((xValue) => xValue > x + width) ||
          yValues.every((yValue) => yValue < y) ||
          yValues.every((yValue) => yValue > y + height)
        );
      });
    }
  }

  // hard boundary check - is any corner outside the print area
  return configurationCorners.some(({ x, y }) => {
    const { width, height } = boundary.size;
    return x < 0 || x > width || y < 0 || y > height;
  });
}

function validateTextContent(configuration: DraftConfigurationWithTextOrSpecialText) {
  const firstUnsupportedGlyph = configuration.text.content
    .split(configuration.text.style.font.supportedGlyphs!)
    .find(Boolean);

  return {
    unicode: firstUnsupportedGlyph ? getFirstSymbol(firstUnsupportedGlyph) : undefined,
    specialFontHeight:
      isDraftSpecialTextConfiguration(configuration) && configuration.task.size.height < 100,
  };
}

function validateAdultDesignsOnChildProducts(
  { design }: DraftDesignConfiguration,
  productType: ProductType
) {
  return {
    adultContent: design.safeSearch !== 'STRICT' && getIsAdultDesignsVerboten(productType),
  };
}

export function validateConfiguration(
  configuration: DraftConfiguration,
  size: Size,
  productType: ProductType
): DraftError {
  const boundary = validateBoundaries(configuration, size);

  const commonValidations = {
    dpiBound: !boundary && validateMaxScale(configuration, size),
    maxBound: !boundary && validateMaxBound(configuration, size),
    boundary: boundary,
    api: '', // remove the api error on every change as we can't validate it
  };

  let typeSpecificValidations: DraftError;

  if (isDraftDesignConfiguration(configuration)) {
    typeSpecificValidations = validateAdultDesignsOnChildProducts(configuration, productType);
  } else {
    typeSpecificValidations = validateTextContent(configuration);
  }

  return { ...commonValidations, ...typeSpecificValidations };
}
